// Canonical production domain — used ONLY for SEO tags (canonical, JSON-LD, OG).
// API calls use REACT_APP_BACKEND_URL separately (see lib/api.js), so this is safe to hardcode.
export const SITE_URL = "https://kerrys.dance";
export const SITE_NAME = "Kerry's";
export const SITE_TAGLINE = "Dance & Performing Arts School in Whitstable";

export const CONTACT = {
  phone: "07904 530364",
  phoneHref: "tel:+447904530364",
  email: "info@kerrys.dance",
  emailHref: "mailto:info@kerrys.dance",
  address: "76 Castle Road, Whitstable, Kent CT5 2EB",
};

export const SOCIALS = {
  facebook: "https://www.facebook.com/kerrys.dance",
  instagram: "https://www.instagram.com/kerrys.dance",
};

export const BOOKING_URL = "https://newcomb-theatre-arts.class4kids.co.uk/";

// Link parents to your Google Business Profile to read/leave reviews.
export const REVIEW_GOOGLE_URL = "https://g.page/r/CUH3qerrcOGiEAI/review";

export const TERM = {
  label: "Autumn term",
  start: "7th September",
  end: "11th December",
  startISO: "2026-09-07",
  endISO: "2026-12-11",
  halfTerm: "19th October \u2013 1st November",
  line: "Autumn term: 7th September \u2013 11th December",
  halfTermLine: "Half term break: 19th October \u2013 1st November (no classes)",
  shortLine: "Autumn term 7 Sept \u2013 11 Dec \u00b7 half term 19 Oct \u2013 1 Nov",
};

// Days until term starts: positive = upcoming, 0 = today, negative = already started
export const daysUntilTerm = () => {
  const [y, m, d] = TERM.startISO.split("-").map(Number);
  const start = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((start - today) / 86400000);
};

export const PRIVATE_LESSONS = {
  price: "£35",
  per: "per hour",
  venue: "Treetops Dance Studio",
  days: "Wednesdays & Fridays",
  note: "Venue included — local to Whitstable",
};

export const VENUES = [
  {
    id: "umbrella",
    name: "The Umbrella Centre",
    address: "St Mary's Hall, Oxford Street, Whitstable, Kent CT5 1DD",
    short: "Oxford Street, Whitstable",
    tag: "Weekly classes · Mondays & Tuesdays",
    mapsUrl: "https://www.google.com/maps/dir/?api=1&destination=The+Umbrella+Centre+Oxford+Street+Whitstable",
    embedUrl: "https://www.google.com/maps?q=The+Umbrella+Centre,+Oxford+Street,+Whitstable&output=embed",
    blurb:
      "A much-loved community hub in the heart of Whitstable — home to Monday's Happy Feet, junior and primary classes, plus Tuesday's intermediate classes, right on the high street.",
    notes: ["Heart of the town centre", "Waiting area for parents", "Buggy friendly"],
    privateOnly: false,
  },
  {
    id: "school",
    name: "The Whitstable School – Dance Studio",
    address: "The Whitstable School, Bellevue Road, Whitstable, Kent CT5 1PX",
    short: "Bellevue Road, Whitstable",
    tag: "Weekly classes · Monday & Wednesday evenings",
    mapsUrl: "https://www.google.com/maps/dir/?api=1&destination=The+Whitstable+School+Bellevue+Road+Whitstable",
    embedUrl: "https://www.google.com/maps?q=The+Whitstable+School,+Bellevue+Road,+Whitstable&output=embed",
    blurb:
      "A 14-metre mirrored dance studio with a sprung wooden floor and ballet barre inside The Whitstable School — home to Monday evening's senior classes and Wednesday's intermediate modern and ballet.",
    notes: ["Sprung floor, mirrors & ballet barre", "Ample parking on site", "Easy drop-off on Bellevue Road"],
    privateOnly: false,
  },
  {
    id: "pool",
    name: "Whitstable Swimming Pool – Dance Studio",
    address: "Tower Parade, Whitstable, Kent CT5 2BJ",
    short: "Tower Parade, Whitstable",
    tag: "Weekly classes · Fridays",
    mapsUrl: "https://www.google.com/maps/dir/?api=1&destination=Whitstable+Swimming+Pool+Tower+Parade+Whitstable",
    embedUrl: "https://www.google.com/maps?q=Whitstable+Swimming+Pool,+Tower+Parade,+Whitstable&output=embed",
    blurb:
      "A proper dance studio tucked inside the town's swimming pool complex — home to our Friday senior classes, moments from the seafront.",
    notes: ["Purpose-built dance studio", "Parking close by", "Easy drop-off"],
    privateOnly: false,
  },
  {
    id: "treetops",
    name: "Treetops Dance Studio",
    address: "Chill At Treetops, Wraik Hill, Whitstable, Kent CT5 3BY",
    short: "Wraik Hill, Whitstable",
    tag: "Private lessons · Wednesdays & Fridays",
    mapsUrl: "https://www.google.com/maps/dir/?api=1&destination=Chill+At+Treetops+Wraik+Hill+Whitstable",
    embedUrl: "https://www.google.com/maps?q=Chill+At+Treetops,+Wraik+Hill,+Whitstable&output=embed",
    blurb:
      "A calm, tucked-away studio space at Chill At Treetops on Wraik Hill — home of our one-to-one private lessons. Venue hire is included in the £35 rate.",
    notes: ["Private lessons only", "By arrangement with Kerry", "Included in the £35/hr rate"],
    privateOnly: true,
  },
];

export const STYLE_META = {
  "Early Years": {
    icon: "Baby",
    badge: "bg-rose-200 text-rose-950 border-foreground/50",
    dot: "bg-rose-400",
    blurb: "Happy Feet — first ballet steps and the very beginnings of tap. Music, movement and rhythm for our tiniest dancers.",
    ages: "2–4 yrs",
  },
  Ballet: {
    icon: "Sparkles",
    badge: "bg-teal-200 text-teal-950 border-foreground/50",
    dot: "bg-sky-500",
    blurb: "Graceful, strong and precise — classical ballet training following the ISTD syllabus, from age 2 to 18.",
    ages: "2–18 yrs",
  },
  Tap: {
    icon: "Footprints",
    badge: "bg-yellow-200 text-yellow-950 border-foreground/50",
    dot: "bg-amber-400",
    blurb: "Rhythm you can hear! ISTD tap builds musicality, sharp footwork and serious fun from the very first shuffle.",
    ages: "4–18 yrs",
  },
  Modern: {
    icon: "Star",
    badge: "bg-orange-200 text-orange-950 border-foreground/50",
    dot: "bg-orange-400",
    blurb: "Energetic, expressive theatre dance — kicks, leaps and turns, trained through the ISTD modern syllabus.",
    ages: "6–18 yrs",
  },
  "Musical Theatre": {
    icon: "Mic",
    badge: "bg-emerald-200 text-emerald-950 border-foreground/50",
    dot: "bg-teal-500",
    blurb: "Sing, dance and act — triple-threat training with West End numbers, characterisation and bags of confidence.",
    ages: "4–18 yrs",
  },
  "Street Dance": {
    icon: "Zap",
    badge: "bg-lime-200 text-lime-950 border-foreground/50",
    dot: "bg-lime-500",
    blurb: "Commercial and hip hop styles, danced to the music they actually listen to — sharp, current and full of energy.",
    ages: "4–18 yrs",
  },
  Contemporary: {
    icon: "Wind",
    badge: "bg-cyan-200 text-cyan-950 border-foreground/50",
    dot: "bg-cyan-500",
    blurb: "Fluid, grounded and expressive — contemporary technique woven into our senior modern classes.",
    ages: "11–18 yrs",
  },
  Singing: {
    icon: "Music",
    badge: "bg-sky-200 text-sky-950 border-foreground/50",
    dot: "bg-sky-500",
    blurb: "Find your voice — vocal technique, harmonies and performance confidence. Wednesday lessons joining the timetable soon.",
    ages: "All ages",
  },
  "Audition Prep": {
    icon: "Target",
    badge: "bg-amber-200 text-amber-950 border-foreground/50",
    dot: "bg-amber-500",
    blurb: "Focused coaching for vocational college and professional auditions — solos, technique polish and walking in ready.",
    ages: "11+ yrs",
  },
  "Body Conditioning": {
    icon: "Dumbbell",
    badge: "bg-stone-200 text-stone-950 border-foreground/50",
    dot: "bg-stone-500",
    blurb: "Strength, flexibility and stamina work that makes every other class easier — dancers' cross-training, minus the boring bits.",
    ages: "11+ yrs",
  },
};

export const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "pm" : "am";
  const hh = h % 12 || 12;
  return m === 0 ? `${hh}${ampm}` : `${hh}:${String(m).padStart(2, "0")}${ampm}`;
};

export const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

export const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1629219222026-e48dcc036c3f?w=1200&q=80";
